import { useEffect, useMemo } from "react";

import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { panelWrapperCss } from "@/view/ActivityPanel/ActivityPanel.style";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import PanelHeader from "@/view/PanelHeader";
import ParentTeacherWritePrompt from "@/view/ParentTeacherWritePrompt";
import StudentWritePrompt from "@/view/StudentWritePrompt";
import getCurrentAssignment from "@/view/utils/getCurrentAssignment";
import getRelevantWritePromptClassrooms from "@/view/utils/getRelevantWritePromptClassrooms";
import useFocusManager from "@/view/utils/useFocusManager";
import useLegacyWritePromptGet from "@/view/utils/useLegacyWritePromptGet";
import { titleCss } from "./WritePromptContainer.style";

const propTypes = {
  classrooms: PropTypes.arrayOf(PropTypes.object),
  classroomListLoading: PropTypes.bool,
  closeButtonEventProperties: PropTypes.object,
  currentArticleLevel: PropTypes.object,
  data: PropTypes.object,
  shouldFocusCloseButtonRef: PropTypes.object,
  legacyArticleLevelHeaderId: PropTypes.number,
  legacyArticleLevelId: PropTypes.number,
  onClickCloseButton: PropTypes.func,
  studentId: PropTypes.number,
  userProperties: PropTypes.object,
  assignments: PropTypes.array,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unique_id: PropTypes.string,
  }),
  contentId: PropTypes.string,
};

const WritePromptContainer = ({
  classroomListLoading,
  classrooms,
  closeButtonEventProperties,
  currentArticleLevel,
  data,
  legacyArticleLevelHeaderId,
  legacyArticleLevelId,
  onClickCloseButton = () => null,
  shouldFocusCloseButtonRef,
  studentId,
  userProperties,
  assignments,
  contentId,
  user,
}) => {
  const router = useRouter();
  const {
    query: { classroomId, levelId, writePromptId, selectedAssignmentId },
  } = router;

  const [
    fetchLegacyWritePrompts,
    { data: legacyWritePrompts, loading: legacyWritePromptsLoading },
  ] = useLegacyWritePromptGet(legacyArticleLevelId);

  const queries = {
    classroomId: "classroomId",
    activityPanel: "activityPanel",
    writePromptId: "writePromptId",
  };
  const { isTeacher, isParent } = userProperties;

  useEffect(() => {
    fetchLegacyWritePrompts();
  }, [legacyArticleLevelId]);

  const { focusElement } = useFocusManager();

  // Return focus to the back button as the url params and the state of the panel changes
  useEffect(() => {
    focusElement(focusElementRefKeys.activityPanelBackButton);
  }, [classroomId, writePromptId]);

  const deleteParams = (queries = []) => {
    const query = { ...router.query };
    // Remove all query params from the queries array content.
    queries.forEach((param) => delete query[param]);

    // Without the write prompt query params, the user will return to the activity picker.
    router.replace({ pathname: router.pathname, query }, null, {
      shallow: true,
    });
  };

  const backButtonEventProperties = {
    objectType: "WritePrompt",
    actionName: "Navigate",
    streamProperties: {
      content_type: "Article",
      description: "Navigate back to Activity Picker from write prompt",
      content_id: levelId,
      object_id: writePromptId,
    },
  };

  // Return an array of write prompt objects with a consistent shape
  const mergePrompts = useMemo(
    () =>
      (defaultPrompt, additionalWritePrompts = []) => {
        const prompts = additionalWritePrompts.map((prompt) => {
          const { collections, teacher, text } = prompt;
          return {
            prompt_id: prompt.id,
            collections,
            date_modified: prompt?.date_modified ?? null,
            id: prompt.content_id,
            is_default_prompt: prompt.content_id === defaultPrompt?.id,
            copiedAssignmentCreator: prompt.copied_assignment_creator,
            teacher,
            questions:
              prompt.content_id === defaultPrompt?.id
                ? defaultPrompt?.questions
                : [{ question: text }],
          };
        });

        return prompts;
      },
    [data?.id, JSON.stringify(legacyWritePrompts)],
  );

  /**
   * Using the default prompt returned from langston, find its match amongst the
   * legacy write prompts, and return the collection of that matching prompt.
   * This should be updated once collections are returned from Langston
   */
  const currentCollection = (defaultPrompt, writePrompts) => {
    const matchingLegacyPrompt = writePrompts.find((prompt) => {
      return prompt.id === defaultPrompt?.id;
    });

    return matchingLegacyPrompt?.collections?.[0];
  };

  const fetchedWritePrompts = mergePrompts(data, legacyWritePrompts);
  const collectionId = currentCollection(data, fetchedWritePrompts)?.id;

  // Limit student classroom options to assignment classrooms if applicable
  const currentAssignment = getCurrentAssignment(
    assignments,
    selectedAssignmentId,
  );
  const classroomOptions = studentId
    ? getRelevantWritePromptClassrooms(classrooms, currentAssignment)
    : classrooms;

  // If there is only one relevant classroom, set it as query param to avoid
  // unnecessary classroom option list
  if (studentId && classroomOptions?.length === 1 && !classroomId) {
    const { query } = router;
    const writeClassroomId = classroomOptions[0]?.id;
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...query,
          classroomId: writeClassroomId,
        },
      },
      null,
      { shallow: true },
    );
  }

  return (
    <div css={panelWrapperCss}>
      <PanelHeader
        backButtonEventProperties={backButtonEventProperties}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        onClickBackButton={() =>
          deleteParams([
            queries.activityPanel,
            queries.classroomId,
            queries.writePromptId,
          ])
        }
        onClickCloseButton={onClickCloseButton}
      >
        <h2 css={titleCss}>Write prompt</h2>
      </PanelHeader>
      {studentId && (
        <StudentWritePrompt
          classrooms={classroomOptions}
          classroomListLoading={classroomListLoading}
          collectionId={collectionId}
          deleteParams={deleteParams}
          fetchedWritePrompts={fetchedWritePrompts}
          legacyArticleLevelHeaderId={legacyArticleLevelHeaderId}
          legacyArticleLevelId={legacyArticleLevelId}
          studentId={studentId}
          assignments={assignments}
          contentId={contentId}
          currentLevel={currentArticleLevel}
          user={user}
        />
      )}
      {(isParent || isTeacher) && (
        // TODO verify Acceptance Criteria for parent accounts
        <ParentTeacherWritePrompt
          classrooms={classroomOptions}
          collectionId={collectionId}
          currentArticleLevel={currentArticleLevel}
          fetchLegacyWritePrompts={fetchLegacyWritePrompts}
          fetchedWritePrompts={fetchedWritePrompts}
          isParent={isParent}
          legacyArticleLevelHeaderId={legacyArticleLevelHeaderId}
          legacyArticleLevelId={legacyArticleLevelId}
          legacyWritePromptsLoading={legacyWritePromptsLoading}
        />
      )}
    </div>
  );
};

WritePromptContainer.propTypes = propTypes;
export default WritePromptContainer;
